import { Component, OnInit, TemplateRef } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { AjaxService } from 'src/app/service/ajax.service';
import { UtilService } from 'src/app/service/util.service';
import { API } from 'src/environments/environment';

interface TipoServico {
  id: number;
  idEncrypted: string;
  nome: string;
  ativo: boolean;
}

@Component({
  selector: 'app-cadastro-tipo-servico',
  templateUrl: './cadastro-tipo-servico.component.html',
  styleUrls: ['./cadastro-tipo-servico.component.scss']
})
export class CadastroTipoServicoComponent implements OnInit {

  formTipoServico: FormGroup;
  tiposServico: TipoServico[] = [];
  tiposServicoFiltrados: TipoServico[] = [];
  tiposServicoPaginados: TipoServico[] = [];
  filtroNome: string = '';
  filtroStatus: string = '';
  modalRef: NgbModalRef;
  tipoServicoSelecionado: TipoServico | null = null;
  loading: boolean = false;
  paginaAtual: number = 1;
  itensPorPagina: number = 10;

  constructor(
    private fb: FormBuilder,
    private modalService: NgbModal,
    private ajaxService: AjaxService,
    private util: UtilService
  ) { }

  ngOnInit(): void {
    this.inicializarFormulario();
    this.carregarTiposServico();
  }

  inicializarFormulario(): void {
    this.formTipoServico = this.fb.group({
      nome: ['', [Validators.required, Validators.minLength(3)]]
    });
  }

  carregarTiposServico(): void {
    const url = API + 'empresa/TipoServico/Consultar';
    this.loading = true;
    this.util.showLoading();
    this.ajaxService.get(url).subscribe({
      next: (response) => {
        this.loading = false;
        this.util.closeLoading();
        const lista = response?.data && Array.isArray(response.data) ? response.data : [];
        this.tiposServico = lista
          .filter((item: any) => item && typeof item === 'object')
          .map((item: any) => ({
            id: Number(item.id || 0),
            idEncrypted: this.toText(item.idEncrypted),
            nome: this.toText(item.descricao || item.nome),
            ativo: this.parseBoolean(item.ativo)
          }))
          .filter((item: TipoServico) => item.id > 0);

        this.aplicarFiltros();
      },
      error: () => {
        this.loading = false;
        this.util.closeLoading();
        this.tiposServico = [];
        this.tiposServicoFiltrados = [];
        this.util.toasterShowError('Erro', 'Não foi possível carregar os tipos de serviço.');
      }
    });
  }

  openModal(content: TemplateRef<any>, isNew: boolean = false): void {
    if (isNew) {
      this.tipoServicoSelecionado = null;
      this.limparFormulario();
    }
    this.modalRef = this.modalService.open(content, { size: 'lg' });
  }

  onSubmit(): void {
    if (this.formTipoServico.valid) {
      this.salvarTipoServico();
    } else {
      this.marcarCamposInvalidos();
      this.util.toasterShowError('Atenção', 'Preencha os campos obrigatórios.');
    }
  }

  limparFormulario(): void {
    this.formTipoServico.reset({ nome: '' });
    this.formTipoServico.markAsPristine();
    this.formTipoServico.markAsUntouched();
  }

  marcarCamposInvalidos(): void {
    Object.keys(this.formTipoServico.controls).forEach(key => {
      const control = this.formTipoServico.get(key);
      if (control?.invalid) {
        control.markAsTouched();
      }
    });
  }

  aplicarFiltros(): void {
    this.tiposServicoFiltrados = this.tiposServico.filter(tipoServico => {
      const matchNome = !this.filtroNome ||
        tipoServico.nome.toLowerCase().includes(this.filtroNome.toLowerCase());
      const matchStatus = !this.filtroStatus ||
        (this.filtroStatus.toLowerCase() === 'ativo' && tipoServico.ativo) ||
        (this.filtroStatus.toLowerCase() === 'inativo' && !tipoServico.ativo);

      return matchNome && matchStatus;
    });

    this.paginaAtual = 1;
    this.atualizarPaginacao();
  }

  limparFiltros(): void {
    this.filtroNome = '';
    this.filtroStatus = '';
    this.aplicarFiltros();
  }

  editarTipoServico(tipoServico: TipoServico): void {
    this.tipoServicoSelecionado = tipoServico;
    this.formTipoServico.patchValue({
      nome: tipoServico.nome
    });
  }

  alterarStatus(tipoServico: TipoServico): void {
    const novoAtivo = !tipoServico.ativo;
    const payload = {
      idServico: tipoServico.idEncrypted || String(tipoServico.id),
      isAtivar: novoAtivo
    };

    const url = API + 'empresa/TipoServico/ControlarStatusAtivo';
    this.util.showLoading();
    this.ajaxService.post(url, payload).subscribe({
      next: (response) => {
        this.util.closeLoading();
        if (response?.hasErrors) {
          this.util.toasterShowError('Erro', response?.message || 'Não foi possível atualizar o status.');
          return;
        }

        tipoServico.ativo = novoAtivo;
        this.aplicarFiltros();
        this.util.toasterShowSuccess('Sucesso', novoAtivo ? 'Serviço ativado com sucesso!' : 'Serviço desativado com sucesso!');
      },
      error: () => {
        this.util.closeLoading();
        this.util.toasterShowError('Erro', 'Erro ao atualizar status do serviço.');
      }
    });
  }

  getStatusClass(ativo: boolean): string {
    return ativo ? 'badge bg-success' : 'badge bg-secondary';
  }

  isFieldInvalid(fieldName: string): boolean {
    const control = this.formTipoServico.get(fieldName);
    return control?.invalid && control?.touched;
  }

  getFieldError(fieldName: string): string {
    const control = this.formTipoServico.get(fieldName);
    if (control?.errors) {
      if (control.errors['required']) return 'Campo obrigatório';
      if (control.errors['minlength']) return `Mínimo ${control.errors['minlength'].requiredLength} caracteres`;
    }
    return '';
  }

  get totalPaginas(): number {
    return Math.ceil(this.tiposServicoFiltrados.length / this.itensPorPagina) || 1;
  }

  alterarItensPorPagina(): void {
    this.paginaAtual = 1;
    this.atualizarPaginacao();
  }

  paginaAnterior(): void {
    if (this.paginaAtual > 1) {
      this.paginaAtual--;
      this.atualizarPaginacao();
    }
  }

  proximaPagina(): void {
    if (this.paginaAtual < this.totalPaginas) {
      this.paginaAtual++;
      this.atualizarPaginacao();
    }
  }

  private salvarTipoServico(): void {
    const formValue = this.formTipoServico.value;
    const payload = {
      ID_TIPO_CONSULTA: this.tipoServicoSelecionado?.id || 0,
      DESCRICAO: this.toText(formValue.nome),
      ATIVO: this.tipoServicoSelecionado ? this.tipoServicoSelecionado.ativo : true
    };

    const url = API + 'empresa/TipoServico/SaveAsync';
    this.util.showLoading();
    this.ajaxService.post(url, payload).subscribe({
      next: (response) => {
        this.util.closeLoading();
        if (response?.hasErrors) {
          this.util.toasterShowError('Erro', response?.message || 'Não foi possível salvar o serviço.');
          return;
        }

        this.util.toasterShowSuccess('Sucesso', 'Serviço salvo com sucesso!');
        this.modalRef?.close();
        this.carregarTiposServico();
      },
      error: () => {
        this.util.closeLoading();
        this.util.toasterShowError('Erro', 'Erro ao salvar o serviço.');
      }
    });
  }

  private parseBoolean(value: any): boolean {
    if (typeof value === 'boolean') return value;
    if (typeof value === 'number') return value === 1;
    const text = this.toText(value).toLowerCase();
    return text === '1' || text === 'true' || text === 'sim' || text === 'yes';
  }

  private toText(value: any): string {
    if (value === null || value === undefined) return '';
    return String(value).trim();
  }

  private atualizarPaginacao(): void {
    const inicio = (this.paginaAtual - 1) * this.itensPorPagina;
    const fim = inicio + this.itensPorPagina;
    this.tiposServicoPaginados = this.tiposServicoFiltrados.slice(inicio, fim);
  }
} 